import React from 'react';
import { Box, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { GlassButton } from 'dashboard/components/common';

/**
 * The menu links for the header or the footer.
 *
 * Order matters — this is a nav bar, and left-to-right is the reading order a
 * visitor gets — so the rows move up and down rather than being sorted for
 * the user. Drag would be nicer; arrows are unambiguous, work on touch, and
 * are reachable from the keyboard, which matters more for a list of four.
 *
 * Both internal paths and external addresses are allowed — brands link out to
 * social, a booking system, a supplier. What is refused is anything that
 * executes or hides where it goes: `javascript:`, `data:`, and
 * `//evil.example`, which reads like a path and navigates off-site.
 *
 * This check mirrors the server's. It existed here first and still said
 * "must start with /" after the server started accepting external links, so
 * the field showed an error for addresses that saved perfectly well.
 */
const isUsableUrl = (raw) => {
  const url = String(raw || '').trim();
  if (!url) return false;
  if (url.startsWith('//')) return false;
  if (url.startsWith('/') || url.startsWith('#')) return true;
  if (/^(mailto:|tel:)/i.test(url)) return true;
  if (/^https?:\/\//i.test(url)) return true;
  // Any other scheme is refused rather than guessed at.
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return false;
  // No scheme — treated as an external host and upgraded to https on save.
  return url.includes('.');
};

const externalLooking = (raw) => {
  const url = String(raw || '').trim();
  return Boolean(url) && !url.startsWith('/') && !url.startsWith('#') && isUsableUrl(url);
};

export default function LinkListEditor({
  links = [], onChange, addLabel = 'Add link', depth = 0, nestable = false,
}) {
  const set = (i, patch) => onChange(links.map((l, j) => (i === j ? { ...l, ...patch } : l)));

  /**
   * How deep a menu may go, matching the server's own limit.
   *
   * Nobody sensibly needs five, but a shop with categories and
   * sub-categories needs more than one, and that is the owner's call to
   * make, not a shape to guess at in a schema.
   */
  const MAX_DEPTH = 5;

  const move = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= links.length) return;
    const next = [...links];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <Box>
      <Stack spacing='8px'>
        {links.map((link, i) => {
          const children = link.children || [];
          const isParent = children.length > 0;
          return (
          <Box key={i}>
          <Stack direction='row' spacing='8px' alignItems='flex-start'>
            <TextField
              size='small'
              label='Label'
              value={link.label || ''}
              onChange={(e) => set(i, { label: e.target.value })}
              placeholder='About'
              sx={{ flex: '0 0 32%' }}
            />
            <TextField
              size='small'
              label='Address'
              value={link.url || ''}
              onChange={(e) => set(i, { url: e.target.value })}
              placeholder='/about or https://instagram.com/…'
              error={Boolean(link.url) && !isUsableUrl(link.url)}
              helperText={
                link.url && !isUsableUrl(link.url)
                  ? 'That is not an address we can link to.'
                  : externalLooking(link.url)
                    ? 'Opens in a new tab.'
                    : isParent && !link.url
                      // A link whose job is to open a submenu is a heading,
                      // not a destination, so it needs no address of its own.
                      ? 'Optional — this one opens its submenu.'
                      : ' '
              }
              sx={{ flex: 1 }}
            />
            <Stack direction='row' sx={{ pt: '4px' }} flexWrap='wrap'>
              <Tooltip title='Move up'>
                <span>
                  <IconButton size='small' onClick={() => move(i, -1)} disabled={i === 0}>
                    <ArrowUpwardIcon fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title='Move down'>
                <span>
                  <IconButton size='small' onClick={() => move(i, 1)} disabled={i === links.length - 1}>
                    <ArrowDownwardIcon fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
              {nestable && depth < MAX_DEPTH - 1 && (
                <Tooltip title='Add a link under this one'>
                  <IconButton
                    size='small'
                    onClick={() => set(i, { children: [...children, { label: '', url: '/' }] })}
                  >
                    <SubdirectoryArrowRightIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title='Remove'>
                <IconButton
                  size='small'
                  color='error'
                  onClick={() => onChange(links.filter((_, j) => j !== i))}
                >
                  <DeleteIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Anything under this link, indented so the shape of the menu is
              the shape of the list. */}
          {nestable && isParent && (
            <Box sx={{ ml: '22px', mt: '8px', pl: '12px', borderLeft: '2px solid', borderColor: 'divider' }}>
              <LinkListEditor
                links={children}
                onChange={(next) => set(i, { children: next })}
                addLabel='Add under this'
                depth={depth + 1}
                nestable
              />
            </Box>
          )}
          </Box>
          );
        })}
      </Stack>

      {links.length === 0 && (
        <Typography variant='body2' color='text.secondary' mb='10px'>
          No links yet. Visitors will see your site name with no menu.
        </Typography>
      )}

      <GlassButton
        size='small'
        startIcon={<AddIcon />}
        onClick={() => onChange([...links, { label: '', url: '/' }])}
        disabled={links.length >= 20}
        disabledReason={links.length >= 20 ? 'Twenty links is already more than a menu can usefully hold.' : ''}
        sx={{ mt: '10px' }}
      >
        {addLabel}
      </GlassButton>
    </Box>
  );
}
